import React from 'react';
import { Eye, EyeOff, TrendingUp } from 'lucide-react';
import { cn } from '../../lib/utils';
import { nextEyeLevel, eyeVisible } from '../../utils/eyePresence';

export interface InteractiveHUDControlsProps {
  hasInteracted: boolean;
  uiHiddenLevel: number;
  setUiHiddenLevel: React.Dispatch<React.SetStateAction<number>>;
  isCurrentlyIdle: boolean;
  showUiControls: boolean;
  isEyeFlashing: boolean;
  setIsEyeFlashing: (val: boolean) => void;
  lastManualUiShowTimeRef: React.MutableRefObject<number>;
  showMarketsPanel: boolean;
  setShowMarketsPanel: (val: boolean) => void;
  favorites: any[];
  setActiveMarketCategory: (cat: string) => void;
}

export const InteractiveHUDControls: React.FC<InteractiveHUDControlsProps> = ({
  hasInteracted,
  uiHiddenLevel,
  setUiHiddenLevel,
  isCurrentlyIdle,
  showUiControls,
  isEyeFlashing,
  setIsEyeFlashing,
  lastManualUiShowTimeRef,
  showMarketsPanel,
  setShowMarketsPanel,
  favorites,
  setActiveMarketCategory,
}) => {
  if (!hasInteracted) return null;

  return (
    <>
      {/* Hide UI Override Button */}
      <div className={cn(
        "absolute bottom-[calc(5.5rem+env(safe-area-inset-bottom,0px))] right-4 md:bottom-12 md:right-10 z-50 flex flex-col items-center gap-3 transition-all duration-1000",
        !eyeVisible(uiHiddenLevel, isCurrentlyIdle, hasInteracted) || (!showUiControls && uiHiddenLevel === 0) ? "opacity-0 invisible pointer-events-none" : "opacity-100 visible pointer-events-auto"
      )}>
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (uiHiddenLevel === 0) {
              setIsEyeFlashing(true);
              setTimeout(() => setIsEyeFlashing(false), 1000);
            } else {
              lastManualUiShowTimeRef.current = Date.now();
            }
            setUiHiddenLevel((prev) => nextEyeLevel(prev));
          }}
          className={cn(
            "p-3 md:p-4 rounded-full border transition-all duration-300 flex items-center justify-center backdrop-blur-sm shadow-[0_0_10px_rgba(0,0,0,0.2)]",
            isEyeFlashing
              ? "bg-[#00d9ff]/30 border-[#00d9ff] text-[#00d9ff] scale-110 shadow-[0_0_20px_rgba(0,217,255,0.5)]"
              : uiHiddenLevel > 0 
                ? "bg-[#00d9ff]/10 border-[#00d9ff]/30 text-[#00d9ff]/80 hover:text-[#00d9ff] hover:bg-[#00d9ff]/20" 
                : "bg-[#0a0e27]/60 border-[#00d9ff]/30 text-[#00d9ff]/60 hover:text-[#00d9ff]/90 hover:border-[#00d9ff]/50 hover:bg-[#0a0e27]/80"
          )}
          title={uiHiddenLevel > 0 ? "Mostrar Interface" : "Ocultar Interface"}
        >
          {uiHiddenLevel > 0 ? <Eye size={24} className="md:w-6 md:h-6 opacity-90" /> : <EyeOff size={24} className="md:w-6 md:h-6 opacity-90" />}
        </button>
      </div>

      {/* Markets Overlay Trigger Button */}
      <div className={cn(
        "absolute bottom-[calc(5.5rem+env(safe-area-inset-bottom,0px))] left-4 md:bottom-12 md:left-10 z-50 flex flex-col items-center gap-3 transition-all duration-1000",
        isCurrentlyIdle || uiHiddenLevel === 2 || !showUiControls ? "opacity-0 invisible pointer-events-none" : "opacity-100 visible pointer-events-auto"
      )}>
        <button
          onClick={(e) => {
            e.stopPropagation();
            setShowMarketsPanel(true);
            const currentFavs = Array.isArray(favorites) ? favorites : [];
            if (currentFavs.length > 0) {
              setActiveMarketCategory('Favoritos');
            } else {
              setActiveMarketCategory('Criptomoedas');
            }
          }}
          className={cn(
            "p-3 md:p-4 rounded-full border transition-all duration-300 flex items-center justify-center backdrop-blur-sm shadow-[0_0_10px_rgba(0,0,0,0.2)] cursor-pointer",
            showMarketsPanel
              ? "bg-[#00d9ff]/30 border-[#00d9ff] text-[#00d9ff] scale-110 shadow-[0_0_20px_rgba(0,217,255,0.5)]"
              : "bg-[#0a0e27]/60 border-[#00d9ff]/30 text-[#00d9ff]/60 hover:text-[#00d9ff]/90 hover:border-[#00d9ff]/50 hover:bg-[#0a0e27]/80"
          )}
          title="Mercados e Favoritos"
        >
          <TrendingUp size={24} className="md:w-6 md:h-6 opacity-90" />
        </button>
      </div>
    </>
  );
};
